import React, { useState } from "react";
import { Download, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api, API } from "../lib/api";

export const DownloadProgramBox = ({ slug, programName }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  const triggerBrowserDownload = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "Programma-Social-Media-Marketing.pdf";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
    if (!emailOk) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/program/request", {
        email: email.trim(),
        slug,
        name: name.trim() || null,
        marketing_consent: consent,
      });
      const downloadUrl = res.data?.download_url || `${API}/program/${slug}/download`;
      triggerBrowserDownload(downloadUrl);
      setDownloadUrl(downloadUrl);
      setEmailSent(Boolean(res.data?.email_sent));
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Si è verificato un errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setError(null);
    }, 250);
  };

  return (
    <>
      <div
        data-testid="download-program-box"
        className="relative overflow-hidden rounded-2xl border border-lime/30 bg-[#0c0c0c] p-6 lg:p-8"
        style={{ borderColor: "rgba(212,255,61,0.3)" }}
      >
        <div
          className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl opacity-20"
          style={{ background: "#d4ff3d" }}
        />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-lime/10" style={{ backgroundColor: "rgba(212,255,61,0.12)" }}>
            <FileText size={26} style={{ color: "#d4ff3d" }} />
          </div>
          <div className="flex-1">
            <div className="font-mono-label text-[11px] uppercase tracking-widest text-white/40 mb-2">
              Programma in PDF
            </div>
            <h3 className="font-display text-xl lg:text-2xl text-white leading-tight">
              Scarica il programma completo del corso
            </h3>
            <p className="text-white/60 text-sm mt-2 leading-relaxed">
              Tutti i moduli, le ore e cosa porterai a casa — in un PDF da consultare con calma.
            </p>
          </div>
          <div className="flex-shrink-0">
            <Button
              data-testid="download-program-open-btn"
              onClick={() => setOpen(true)}
              className="rounded-full px-6 py-6 text-sm font-bold"
              style={{ backgroundColor: "#d4ff3d", color: "#0a0a0a" }}
            >
              <Download size={18} className="mr-2" />
              Scarica il programma del corso
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : resetAndClose())}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white sm:max-w-md" data-testid="download-program-dialog">
          {done ? (
            <div className="py-6 text-center" data-testid="download-program-success">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: "rgba(212,255,61,0.12)" }}>
                <CheckCircle2 size={30} style={{ color: "#d4ff3d" }} />
              </div>
              <h3 className="font-display text-2xl text-white">Ecco il tuo programma!</h3>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">
                {emailSent ? (
                  <>Il download è partito. Ti abbiamo inviato una copia del PDF anche via email a{" "}
                  <span className="text-white">{email.trim()}</span> — controlla la posta (anche lo spam).</>
                ) : (
                  <>Il download del PDF è partito. Se non si avvia, usa il pulsante qui sotto.</>
                )}
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <a
                  data-testid="download-program-redownload-link"
                  href={downloadUrl}
                  download="Programma-Social-Media-Marketing.pdf"
                  className="inline-flex items-center rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-white hover:bg-white/5 transition-colors"
                >
                  <Download size={16} className="mr-2" /> Scarica di nuovo
                </a>
                <Button
                  data-testid="download-program-close-btn"
                  onClick={resetAndClose}
                  className="rounded-full px-6"
                  style={{ backgroundColor: "#d4ff3d", color: "#0a0a0a" }}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-white">
                  Scarica il programma di {programName}
                </DialogTitle>
              </DialogHeader>
              <p className="text-white/55 text-sm -mt-1 mb-2">
                Inserisci la tua email: ricevi subito il PDF e te ne mandiamo una copia in posta.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">Nome (facoltativo)</label>
                  <Input
                    data-testid="download-program-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Il tuo nome"
                    className="mt-1 bg-[#131313] border-white/10 text-white placeholder:text-white/30"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">Email *</label>
                  <Input
                    data-testid="download-program-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@email.it"
                    required
                    className="mt-1 bg-[#131313] border-white/10 text-white placeholder:text-white/30"
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    data-testid="download-program-consent-checkbox"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-lime"
                    style={{ accentColor: "#d4ff3d" }}
                  />
                  <span className="text-white/50 text-xs leading-relaxed">
                    Acconsento a ricevere informazioni sui corsi di MyDigitalAction. Niente spam, cancellabile in ogni momento.
                  </span>
                </label>
                {error && (
                  <div data-testid="download-program-error" className="text-red-400 text-sm">
                    {error}
                  </div>
                )}
                <Button
                  data-testid="download-program-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full py-6 text-sm font-bold"
                  style={{ backgroundColor: "#d4ff3d", color: "#0a0a0a" }}
                >
                  {loading ? (
                    <><Loader2 size={18} className="mr-2 animate-spin" /> Invio in corso…</>
                  ) : (
                    <><Download size={18} className="mr-2" /> Scarica il programma</>
                  )}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DownloadProgramBox;
